import { CdkScrollable, ScrollDispatcher, ScrollDispatcherTarget } from '@angular/cdk/scrolling';
import { ViewportScroller } from '@angular/common';
import {
  afterNextRender,
  AfterRenderRef,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EffectRef,
  ElementRef,
  inject,
  input,
  InputSignal,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatFabButton } from '@angular/material/button';
import { IconComponent } from '@libs/components/icons';
import { ViewportService } from '@libs/shared/services';

@Component({
  selector: 'ui-scroll-top',
  templateUrl: './scroll-top.component.html',
  styleUrls: ['./scroll-top.component.scss', './scroll-top.component.lekkerland-2026.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    MatFabButton,
  ],
  host: {
    '[class.hidden]': 'isHidden()',
    '(window:scroll)': 'updateVisibility()',
  },
})
export class ScrollTopComponent {
  private readonly viewportService: ViewportService = inject(ViewportService);
  private readonly viewportScroller: ViewportScroller = inject(ViewportScroller);
  private readonly elementRef: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly scrollDispatcher: ScrollDispatcher = inject(ScrollDispatcher);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private scrollContainer: CdkScrollable | undefined;
  private readonly _isHidden: WritableSignal<boolean> = signal<boolean>(true);

  private readonly updateVisibilityOnThresholdChange: EffectRef = effect((): void => {
    this.updateVisibility();
  });

  private readonly initializeScrollContainerAfterRender: AfterRenderRef = afterNextRender({
    read: (): void => {
      this.initializeScrollContainer();
    },
  });

  public readonly isDesktop: Signal<boolean> = toSignal(
    this.viewportService.isDesktop(),
    { initialValue: false },
  );

  public readonly showThresholdPosition: InputSignal<number> = input<number>(500);
  public readonly hideThresholdPosition: InputSignal<number> = input<number>(250);
  public readonly label: InputSignal<string | undefined> = input<string>();
  public readonly isHidden: Signal<boolean> = this._isHidden.asReadonly();

  /** Without a label the button stays compact on every viewport. */
  public readonly showLabel: Signal<boolean> = computed((): boolean => this.isDesktop() && !!this.label());

  public updateVisibility(): void {
    const scrollPosition: number = this.scrollContainer
      ? this.scrollContainer.measureScrollOffset('top')
      : this.viewportScroller.getScrollPosition()[1];

    if (scrollPosition >= this.showThresholdPosition()) {
      this._isHidden.set(false);
    } else if (scrollPosition < this.hideThresholdPosition()) {
      this._isHidden.set(true);
    }
  }

  public doScrollTop(): void {
    if (this.scrollContainer) {
      this.scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });

      return;
    }

    this.viewportScroller.scrollToPosition([0, 0], {
      behavior: 'smooth',
    });
  }

  private initializeScrollContainer(): void {
    this.scrollContainer = this.findNearestScrollContainer();

    this.scrollContainer?.elementScrolled()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this.updateVisibility();
      });

    this.updateVisibility();
  }

  private findNearestScrollContainer(): CdkScrollable | undefined {
    const scrollContainers: ScrollDispatcherTarget[] = this.scrollDispatcher.getAncestorScrollContainers(
      this.elementRef,
    );

    let parentElement: HTMLElement | null = this.elementRef.nativeElement.parentElement;

    while (parentElement) {
      const scrollContainer: CdkScrollable | undefined = scrollContainers.find(
        (container: ScrollDispatcherTarget): container is CdkScrollable =>
          container.getElementRef().nativeElement === parentElement,
      );

      if (scrollContainer) {
        return scrollContainer;
      }

      parentElement = parentElement.parentElement;
    }

    return undefined;
  }
}
